import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE, getPage } from '../seo/pages';

// Runtime <head> manager for the public marketing pages. Updates title / meta /
// canonical / og / twitter / JSON-LD on client-side navigation so Googlebot's JS
// render and the browser tab reflect the current route. It IMPERATIVELY upserts
// existing tags (rather than rendering new ones) to avoid duplicating the
// per-route meta that scripts/prerender-seo.mjs already baked into the served
// HTML for non-JS crawlers. Japan-first: the canonical indexed language is `ja`.
//
// Place one <Seo/> in PortalLayout (route-aware via useLocation) to cover every
// portal/docs route; standalone public pages (e.g. /try) render <Seo path="…"/>.

interface SeoProps {
  path?: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(blocks?: Record<string, unknown>[]) {
  // Remove ALL ld+json (incl. the copy prerender-seo.mjs baked into the served
  // HTML for non-JS crawlers) so JS-rendering crawlers don't see duplicates.
  document.querySelectorAll('script[type="application/ld+json"]').forEach((n) => n.remove());
  (blocks || []).forEach((b) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo-jsonld', '');
    s.textContent = JSON.stringify(b);
    document.head.appendChild(s);
  });
}

const Seo: React.FC<SeoProps> = ({ path }) => {
  const location = useLocation();
  const pathname = path ?? location.pathname;

  useEffect(() => {
    const page = getPage(pathname);
    if (!page) return; // unknown route → leave index.html defaults in place

    const canonical = `${SITE.baseUrl}${page.path}`;
    const ogImage = `${SITE.baseUrl}${SITE.defaultOgImage}`;

    document.documentElement.lang = SITE.lang;
    document.title = page.title;
    upsertMeta('name', 'description', page.description);
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', SITE.siteName);
    upsertMeta('property', 'og:locale', SITE.locale);
    upsertMeta('property', 'og:type', page.path === '/' ? 'website' : 'article');
    upsertMeta('property', 'og:title', page.title);
    upsertMeta('property', 'og:description', page.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImage);

    upsertMeta('name', 'twitter:card', SITE.twitterCard);
    upsertMeta('name', 'twitter:title', page.title);
    upsertMeta('name', 'twitter:description', page.description);
    upsertMeta('name', 'twitter:image', ogImage);

    setJsonLd(page.jsonLd);
  }, [pathname]);

  return null;
};

export default Seo;
